import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Match, SerializableMatch, convertToSerializableMatch } from '../services/matchService';

// Type de l'état de match
interface MatchState {
  currentMatch: SerializableMatch | null;
  matches: SerializableMatch[];
  isMatchStarted: boolean;
  isMatchEnded: boolean;
}

// État initial
const initialState: MatchState = {
  currentMatch: null,
  matches: [],
  isMatchStarted: false,
  isMatchEnded: false
};

// Slice de match
const matchSlice = createSlice({
  name: 'match',
  initialState,
  reducers: {
    setCurrentMatch(state, action: PayloadAction<Match | SerializableMatch>) {
      // Convertir les Timestamps en chaînes ISO pour éviter les erreurs de sérialisation
      const payload = action.payload;
      
      console.log('[REDUX] setCurrentMatch appelé', { 
        payloadId: payload.id,
        payloadType: 'date' in payload && typeof payload.date === 'string' ? 'SerializableMatch' : 'Match',
        dateType: typeof payload.date,
        eventsCount: payload.events?.length || 0
      });
      
      if ('date' in payload && typeof payload.date === 'string') {
        console.log('[REDUX] Payload déjà sérialisable, pas de conversion nécessaire');
        state.currentMatch = payload as SerializableMatch;
      } else {
        console.log('[REDUX] Conversion du payload en format sérialisable');
        state.currentMatch = convertToSerializableMatch(payload as Match);
      }
      
      console.log('[REDUX] Match mis à jour dans le state', {
        matchId: state.currentMatch.id,
        dateType: typeof state.currentMatch.date,
        eventsCount: state.currentMatch.events?.length || 0
      });
    },
    deleteEvent(state, action: PayloadAction<{ eventIndex: number }>) {
      if (state.currentMatch && state.currentMatch.events) {
        const eventToDelete = state.currentMatch.events[action.payload.eventIndex];
        console.log('[REDUX] Deleting event and updating score', {
          event: eventToDelete,
          currentScoreA: state.currentMatch.scoreA,
          currentScoreB: state.currentMatch.scoreB
        });
        
        // Mettre à jour le score si c'était un but
        if (eventToDelete.type === 'goal') {
          if (eventToDelete.team === 'teamA') {
            state.currentMatch.scoreA = Math.max(0, state.currentMatch.scoreA - 1);
            console.log('[REDUX] Updated team A score', {
              oldScore: state.currentMatch.scoreA + 1,
              newScore: state.currentMatch.scoreA
            });
          } else if (eventToDelete.team === 'teamB') {
            state.currentMatch.scoreB = Math.max(0, state.currentMatch.scoreB - 1);
            console.log('[REDUX] Updated team B score', {
              oldScore: state.currentMatch.scoreB + 1,
              newScore: state.currentMatch.scoreB
            });
          }
        }

        // Supprimer l'événement
        state.currentMatch.events = state.currentMatch.events.filter((_, index) => index !== action.payload.eventIndex);
      }
    },
    startMatch(state, action: PayloadAction<string>) {
      if (state.currentMatch) {
        state.currentMatch.status = 'live';
        state.isMatchStarted = true;
      }
    },
    endMatch(state) {
      if (state.currentMatch) {
        state.currentMatch.status = 'finished';
        state.isMatchEnded = true;
      }
    },
    updateScore(state, action: PayloadAction<{ team: 'teamA' | 'teamB'; score: number }>) {
      if (state.currentMatch) {
        if (action.payload.team === 'teamA') {
          state.currentMatch.scoreA = action.payload.score;
        } else {
          state.currentMatch.scoreB = action.payload.score;
        }
      }
    },
    endHalfTime(state) {
      if (state.currentMatch) {
        state.currentMatch.status = 'halftime';
      }
    },
    startSecondHalf(state) {
      if (state.currentMatch) {
        state.currentMatch.status = 'live';
      }
    },
    resetMatch(state) {
      state.currentMatch = null;
      state.isMatchStarted = false;
      state.isMatchEnded = false;
    }
  }
});

export const { 
  setCurrentMatch, 
  startMatch, 
  endMatch, 
  resetMatch, 
  updateScore,
  deleteEvent,
  endHalfTime,
  startSecondHalf
} = matchSlice.actions;

export default matchSlice.reducer;
